import { Link } from "react-router-dom";
import { Hexagon, Sparkles, Video, MessageSquare, Users, BookOpen, ArrowRight, Bot } from "lucide-react";

const features = [
  { icon: MessageSquare, title: "Subject Forums", desc: "Every subject from Algebra to Quantum Mechanics has its own threaded forum. Ask, answer, upvote." },
  { icon: Video, title: "Live Study Rooms", desc: "Jump into a video study room per subject. See who's studying now, share notes, focus together." },
  { icon: Bot, title: "BeeAI Assistant", desc: "Ask Claude, GPT or Gemini for explanations, quizzes and study plans — one click away, always on." },
  { icon: Users, title: "Student Profiles", desc: "Build a profile, add friends, discover others taking the same subjects at your level." },
];

export default function Landing() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="bee-honeycomb-bg absolute inset-0 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 animate-fade-up">
              <div className="inline-flex items-center gap-2 bee-chip mb-6" data-testid="hero-tag">
                <Sparkles size={12} className="text-[color:var(--bee-primary)]" />
                <span className="tracking-wide">Focus. Together. Better.</span>
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[0.95] tracking-tighter">
                Study the whole<br />
                <span className="relative inline-block">
                  <span className="relative z-10">syllabus</span>
                  <span className="absolute bottom-1 left-0 right-0 h-3 bg-[color:var(--bee-primary)]/40 -z-0" />
                </span>{" "}
                with a <span className="text-[color:var(--bee-primary-hover)]">hive</span> of students.
              </h1>
              <p className="mt-6 text-lg text-[color:var(--bee-muted)] max-w-xl leading-relaxed">
                FocusBee gives every high school and university subject its own <b>forum</b>, <b>live study room</b>, and <b>call space</b> — plus an AI tutor that never sleeps. Build a profile, find your people, get unstuck.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register" data-testid="hero-cta" className="bee-btn-primary inline-flex items-center gap-2">
                  Join the hive <ArrowRight size={16} />
                </Link>
                <Link to="/login" data-testid="hero-signin" className="bee-btn-secondary">I already have an account</Link>
              </div>
              <div className="mt-10 flex items-center gap-6 text-sm text-[color:var(--bee-muted)]">
                <div className="flex -space-x-2">
                  {["#F5A623", "#10B981", "#3B82F6", "#EF4444"].map((c) => (
                    <div key={c} className="w-8 h-8 hex-clip border-2 border-white" style={{ background: c }} />
                  ))}
                </div>
                <span><b className="text-[color:var(--bee-text)]">100+</b> subjects · <b className="text-[color:var(--bee-text)]">HS + Uni</b> · Free forever</span>
              </div>
            </div>

            <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
              <div className="relative">
                <div className="absolute inset-0 bg-[color:var(--bee-primary)]/20 translate-x-4 translate-y-4" />
                <img
                  src="https://images.pexels.com/photos/16420457/pexels-photo-16420457.jpeg"
                  alt="Students studying"
                  className="relative w-full h-[440px] object-cover border border-[color:var(--bee-border)]"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white border border-[color:var(--bee-border)] p-4 shadow-lg max-w-[220px]">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 hex-clip bg-[color:var(--bee-primary)] flex items-center justify-center">
                    <Bot size={14} className="text-[color:var(--bee-text)]" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider">BeeAI</span>
                </div>
                <p className="text-xs text-[color:var(--bee-muted)] leading-relaxed">"Let me explain the chain rule with a real example..."</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-2xl mb-12">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[color:var(--bee-muted)] mb-3">Everything you need</p>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">A workspace for every subject you'll ever take.</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {features.map((f, i) => (
            <div key={f.title} data-testid={`feature-${i}`} className="bee-card p-8 group">
              <div className="w-12 h-12 hex-clip bg-[color:var(--bee-surface-alt)] group-hover:bg-[color:var(--bee-primary)] transition-colors flex items-center justify-center mb-5">
                <f.icon size={22} strokeWidth={2} className="text-[color:var(--bee-text)]" />
              </div>
              <h3 className="font-display text-2xl font-semibold mb-2">{f.title}</h3>
              <p className="text-[color:var(--bee-muted)] leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-[#1A1D20] text-white p-10 sm:p-16 relative overflow-hidden">
          <Hexagon size={280} className="absolute -right-16 -top-16 text-[color:var(--bee-primary)]/10" strokeWidth={1} />
          <div className="relative max-w-2xl">
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight">Your syllabus. Your people. Your hive.</h2>
            <p className="mt-4 text-white/70 leading-relaxed">Whether you're prepping for finals, cramming an SAT section, or writing your dissertation — FocusBee has a room for you.</p>
            <div className="mt-8">
              <Link to="/register" data-testid="footer-cta" className="bee-btn-primary inline-flex items-center gap-2">
                Create your free profile <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[color:var(--bee-border)] py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between text-sm text-[color:var(--bee-muted)]">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 hex-clip bg-[color:var(--bee-primary)]" />
            <span>© 2026 FocusBee. Study smarter, together.</span>
          </div>
          <BookOpen size={16} />
        </div>
      </footer>
    </div>
  );
}
